/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name align */

import { ProjectElement }                 from './project.element';

import {
  Blob as FileBlob,
  BlobType
}                                         from '../blob';

import { Project }                        from '../project';

import { ProjectElementFactoryDecorator } from '@/models/project/project.element.factory.decorator';
import { JSONBrowserMetadata }            from '@/screens/ide/json.browser.metadata';
import { basename }                       from '@/util/util';
import { TextEditorMetadata }             from '@/screens/ide/text.editor.metadata';
import { Logger }                         from '@/util/logger';
import { BlobElement }                    from '@/models/project/blob.element';
import { DiamondCrossingsMergeResult }    from '@/models/diamond.crossings.merge.result';
import { PropertiesHolder }               from '@/models/properties.holder';
import { ProjectElementType }             from '@/models/project/project.element.type';
import { DiamondCrossingsElement }        from '@/models/project/diamond.crossings.element';
import { FileBrowserMetadata }            from '@/screens/ide/file.browser.metadata';


/**
 *
 * @class DiamondCrossingsResultElement
 *
 * @brief this project leaf is a diamond crossings and scenario merge result
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      February 2024
 *
 */
@ProjectElementFactoryDecorator('DiamondCrossingsResultElement')
export class DiamondCrossingsResultElement extends BlobElement {

  /** the scenario/commands merge status/result */
  public result: DiamondCrossingsMergeResult|null = null;

  /** overall status */
  public status: string             = '';

  /** seconds to validate */
  public validation_time: number    = 0.0;

  /** seconds to do the merge */
  public merge_time: number         = 0.0;

  /** total time in seconds */
  public total_time: number         = 0.0;

  /** how many errors */
  public error_count: number        = 0.0;

  /* we can't embed 'errors' because for a big diamond crossings file, we might get a big list of errors */

  /** standard constructor */
  constructor(
    project:  Project,
    id:       string,
    type:     BlobType,
    key:      string,
    parent:   ProjectElement,
    publish?: boolean,
    label?:   string,
    data?:    DiamondCrossingsMergeResult
  ) {

    super(project, id, type, key, parent, publish, label, ProjectElementType.DiamondCrossingsResult);

    this.actionKeys = [...this.actionKeys, 'viewerrors'];

    if (data instanceof DiamondCrossingsMergeResult) {

      this.result          = data;

      this.status          = data.status;
      this.validation_time = data.validation_time;
      this.merge_time      = data.merge_time;
      this.total_time      = data.total_time;
      this.error_count     = data.errors.length;

    }

    this.updateIcon();
  }

  /** all are notified (this method) after they are hooked into the project tree */
  public onAdded(): void {

    this.updateIcon();
  }

  /** refresh the icon display for this element */
  public updateIcon(): void {

    if (this.parent()) {

      (this.parent() as DiamondCrossingsElement).updateIcon();
    }

    /* if no errors, we are green! */

    if (this.error_count == 0) {

      this._icon    = 'blob-icons/file-json-20.png';

      this.redraw();

      return ;
    }

    /* there is at least one error somewhere */

    this._icon    = 'blob-icons/file-json-error-20.png';

    this.redraw();

    return ;
  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring DiamondCrossingsResultElement");
  }

  /** convert this element to a simplified form that can be easily serialized */
  public node(init?: any) {

    return super.node({
      status:          this.status,
      validation_time: this.validation_time,
      merge_time:      this.merge_time,
      total_time:      this.total_time,
      error_count:     this.error_count
    });
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any) {

    /* recover the basics */

    super.fromNode(node);

    /* recover the job details */

    this.status          = node.status;
    this.validation_time = node.validation_time;
    this.merge_time      = node.merge_time;
    this.total_time      = node.total_time;
    this.error_count     = node.error_count;

    /* icon is set per the job's known status */

    this.updateIcon();
  }

  /** append these elements properties to the holder */
  public properties(properties?: PropertiesHolder): PropertiesHolder {

    if (!properties) {

      properties = new PropertiesHolder();

    }

    properties.set('Status',           this.status);
    properties.set('Validation (sec)', this.validation_time.toString());
    properties.set('Merge (sec)',      this.merge_time.toString());
    properties.set('Total (sec)',      this.total_time.toString());
    properties.set('# of Errors',      this.error_count.toString());

    /* add in the usual properties */

    return super.properties(properties);
  }

  /**
   * subclasses must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    actionSet.viewerrors = {
      name: 'View Errors',
      icon: 'fas fa-bug',
      disabled: !(this.parent() as DiamondCrossingsElement).hasErrors()
    }

    actionSet.viewinbrowser = {
      name: 'Folder Of',
      icon: 'fas fa-list',
      disabled: false
    };

    actionSet.tools = {

      name: 'Tools',
      items: {

        jsoneditblob:  {
          name: 'JSON View',
          icon: 'fas fa-edit',
          disabled: false
        },

        raweditblob: {
          name: 'Raw View',
          icon: 'fas fa-keyboard',
          disabled: false
        },

        download: {
          name: 'Download',
          icon: 'fas fa-file-download',
          disabled: false
        }
      }
    };

    actionSet.renameblob = {
      name: 'Rename',
      icon: 'fas fa-tag',
      disabled:  this.isBusy()
    };

    actionSet.deleteblob = {
      name: 'Delete',
      icon: 'fas fa-trash-alt',
      disabled:  this.isBusy()
    };

    return actionSet;
  }

  /** subclasses must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    switch (key) {

      case 'viewerrors': {

          (this.parent() as DiamondCrossingsElement).viewErrors();
        }
        break;

      case 'jsoneditblob': {

          this._project.services.ide.launchWindow(new  JSONBrowserMetadata(
            '',
            '',
            this._blobKey,
            'json',
            basename(this._blobKey), '<i class="fas fa-edit"></i>'
          ));

        }
        break;

      case 'raweditblob': {

          this._project.services.ide.launchWindow(new TextEditorMetadata(
            '',
            '',
            this._blobKey
          ));

        }

        break;

      case 'download': {

          super.downloadBlob();

        }

        break;

      case 'renameblob': {

          super.renameBlob();

        }
        break;

      case 'deleteblob': {

          Logger.debug('[DiamondCrossingsResultElement] deleting', this._blobKey, this._blobType);

          super.deleteElements(selection);

        }
        break;

      case 'viewinbrowser': {

          this._project.services.ide.launchWindow(FileBrowserMetadata.build(this._blobKey, this._blobKey));
        }
        break;
    }

  }

}
